import {
  HHTextField,
  HHTypography,
} from '@hinge-health/react-component-library';
import { Autocomplete } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useGetBillingTypesQuery } from '../../../../types';
import { capitalizeFirstLetterAndSpaceSnakeString } from '../../../../utils/bills-utils';
import {
  fieldHasErrors,
  getFormErrorsForContractField,
  getFormErrorsForInnerField,
  innerFieldHasErrors,
} from '../../../../utils/contract-utils';
import {
  RawContractPackageFormValues,
  RawInnerPackageFormValues,
} from '../custom-package-types';

export interface BillingTypeSelectProps {
  name: string;
  label: string;
  index?: number;
}

export const BillingTypeSelect = ({
  name,
  label,
  index,
}: BillingTypeSelectProps): JSX.Element => {
  const { data, error } = useGetBillingTypesQuery();

  const billingTypesOptions = data?.getBillingTypes
    .map(type => ({
      label: capitalizeFirstLetterAndSpaceSnakeString(type.value),
      value: type.value,
    }))
    .sort((a, b) => a.value.localeCompare(b.value));

  const {
    control,
    formState: { errors },
  } = useFormContext();

  const fieldName = name.includes('selectedPackages')
    ? 'billingTypeOverride'
    : name;

  if (error) {
    return (
      <HHTypography hhVariant="body1" color="error">
        Billing types unavailable
      </HHTypography>
    );
  }

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value, ref } }): JSX.Element => {
        const selectedOption =
          billingTypesOptions?.find(option => option.value === value) ?? null;

        return (
          <Autocomplete
            ref={ref}
            fullWidth
            options={billingTypesOptions ?? []}
            onChange={(_, newValue): void => {
              onChange(newValue?.value ?? null);
            }}
            value={selectedOption}
            renderInput={(params): JSX.Element => (
              <HHTextField
                {...params}
                label={label}
                hhVariant="variant-bypass"
                name={fieldName}
                error={
                  fieldHasErrors(
                    fieldName as keyof RawContractPackageFormValues,
                    errors,
                  ) ||
                  innerFieldHasErrors(
                    fieldName as keyof RawInnerPackageFormValues,
                    index ?? 0,
                    errors,
                  )
                }
                helperText={
                  getFormErrorsForContractField(
                    fieldName as keyof RawContractPackageFormValues,
                    errors,
                  ) ||
                  getFormErrorsForInnerField(
                    fieldName as keyof RawInnerPackageFormValues,
                    index ?? 0,
                    errors,
                  )
                }
              />
            )}
          />
        );
      }}
    />
  );
};
